import { createGraph } from './graphModel.js';

/**
 * Id-based difference between two graphs, used by history.
 *
 * Unlike a whole-graph snapshot, applying a diff only touches the nodes and
 * wires it names, so undoing one graph edit never reverts unrelated changes
 * made in between. Removed and added items keep their array index, because
 * node order is export order in the domains' compilers.
 *
 * Shape: { nodes: ListDiff, wires: ListDiff }
 *   ListDiff: { added: [{ index, item }], removed: [{ index, item }], changed: [{ prev, next }] }
 */
function diffList(prevList, nextList) {
    const prevById = new Map(prevList.map((item, index) => [item.id, { item, index }]));
    const nextById = new Map(nextList.map((item, index) => [item.id, { item, index }]));
    const added = [...nextById].filter(([id]) => !prevById.has(id)).map(([, entry]) => structuredClone(entry));
    const removed = [...prevById].filter(([id]) => !nextById.has(id)).map(([, entry]) => structuredClone(entry));
    const changed = [...nextById]
        .filter(([id, entry]) => prevById.has(id) && JSON.stringify(prevById.get(id).item) !== JSON.stringify(entry.item))
        .map(([id, entry]) => ({ prev: structuredClone(prevById.get(id).item), next: structuredClone(entry.item) }));
    return { added, removed, changed };
}

export function diffGraphs(prev, next) {
    const before = prev ?? createGraph();
    const after = next ?? createGraph();
    return { nodes: diffList(before.nodes, after.nodes), wires: diffList(before.wires, after.wires) };
}

export function isEmptyGraphDiff(diff) {
    return ['nodes', 'wires'].every(key =>
        diff[key].added.length === 0 && diff[key].removed.length === 0 && diff[key].changed.length === 0);
}

function applyList(list, { added, removed, changed }, forward) {
    const insert = forward ? added : removed;
    const drop = new Set((forward ? removed : added).map(entry => entry.item.id));
    const replace = new Map(changed.map(entry => forward ? [entry.next.id, entry.next] : [entry.prev.id, entry.prev]));
    const result = list
        .filter(item => !drop.has(item.id))
        .map(item => replace.has(item.id) ? structuredClone(replace.get(item.id)) : item);
    const present = new Set(result.map(item => item.id));
    for (const { index, item } of [...insert].sort((a, b) => a.index - b.index)) {
        if (present.has(item.id)) continue;
        result.splice(Math.min(index, result.length), 0, structuredClone(item));
    }
    return result;
}

/** Apply `diff` forward (prev → next) or backward (next → prev). Returns a new graph. */
export function applyGraphDiff(graph, diff, { backward = false } = {}) {
    const base = graph ?? createGraph();
    return {
        ...base,
        nodes: applyList(base.nodes, diff.nodes, !backward),
        wires: applyList(base.wires, diff.wires, !backward),
    };
}

const onlyChanges = diff => ['nodes', 'wires'].every(key => diff[key].added.length === 0 && diff[key].removed.length === 0);

/** Two change-only diffs can merge into one history entry. */
export function canComposeGraphDiffs(first, second) {
    return onlyChanges(first) && onlyChanges(second);
}

/** Compose change-only diffs: earliest `prev`, latest `next` per id. */
export function composeGraphDiffs(first, second) {
    const compose = key => {
        const byId = new Map(first[key].changed.map(entry => [entry.prev.id, { ...entry }]));
        for (const entry of second[key].changed) {
            const existing = byId.get(entry.next.id);
            byId.set(entry.next.id, { prev: existing?.prev ?? entry.prev, next: entry.next });
        }
        return { added: [], removed: [], changed: [...byId.values()] };
    };
    return { nodes: compose('nodes'), wires: compose('wires') };
}

/** UI objects whose wires a diff touches, at either end (for per-object history filtering). */
export function graphDiffObjectUuids(diff) {
    const wires = [...diff.wires.added, ...diff.wires.removed].map(entry => entry.item)
        .concat(diff.wires.changed.flatMap(entry => [entry.prev, entry.next]));
    return [...new Set(wires.flatMap(wire => [wire.from.object, wire.to.object]).filter(Boolean))];
}
